// src/frontend/pages/admin/Forecasts.jsx
import { useMemo, useState } from "react";
import { DataPageCard, PaginatedTable } from "@common";
import { useOverviewForecastTotal } from "@api/hooks";
import { formatMoney } from "@utils/format";

export default function Forecasts({ date }) {
  // Local pagination state
  const [page, setPage] = useState(1); // 1-based
  const [pageSize, setPageSize] = useState(10);

  // Fetch all forecast data (no server-side pagination)
  const {
    data: forecastData,
    isLoading,
    error,
  } = useOverviewForecastTotal(
    {
      date,
    },
    { enablePolling: true },
  );

  // Map API results -> row shape
  const allClinicRows = useMemo(() => {
    if (!forecastData?.results) return [];

    return forecastData.results.map((clinic) => ({
      clinic: clinic.clinic,
      mtd: clinic.mtd_production || 0,
      forecast: clinic.forecast || 0,
      total: (clinic.mtd_production || 0) + (clinic.forecast || 0),
    }));
  }, [forecastData]);

  const totalItems = allClinicRows.length;

  const columns = [
    {
      key: "clinic",
      header: "CLINIC",
    },
    {
      key: "mtd",
      header: "MTD PRODUCTION",
      render: (r) => formatMoney(r.mtd),
    },
    {
      key: "forecast",
      header: "FORECAST",
      render: (r) => formatMoney(r.forecast),
    },
    {
      key: "total",
      header: "TOTAL",
      render: (r) => formatMoney(r.total),
    },
  ];

  return (
    <DataPageCard
      title="Forecasts at a Glance"
      className="mt-2"
      isLoading={isLoading}
      error={error}
      isEmpty={!date || totalItems === 0}
      loadingMessage="Loading forecasts..."
      emptyMessage={
        !date ? "Please select a date to view forecasts" : `No forecast data available for ${date}`
      }
      showLoadingSpinner={true}
    >
      <PaginatedTable
        mode="client"
        columns={columns}
        data={allClinicRows}
        rowKey={(r) => r.clinic}
        cellClassName="font-semibold text-palette-slate-primary"
        page={page}
        pageSize={pageSize}
        total={totalItems}
        onPageChange={setPage}
        onPageSizeChange={(n) => {
          setPage(1);
          setPageSize(n);
        }}
      />
    </DataPageCard>
  );
}
